import {useAuth} from '../../auth/use-auth';
import {memo, useEffect, useMemo, useRef} from 'react';
import lazyLoader from '@ui/utils/loaders/lazy-loader';
import clsx from 'clsx';
import {useSettings} from '@ui/settings/use-settings';
import dot from 'dot-object';
import {Settings} from '@ui/settings/settings';
import {getScrollParent} from '@react-aria/utils';

interface AdHostProps {
  slot: keyof Omit<NonNullable<Settings['ads']>, 'disable'>;
  className?: string;
}
export function AdHost({slot, className}: AdHostProps) {
  const settings = useSettings();
  const {isSubscribed, getPermission} = useAuth();
  const adCode = useMemo(() => {
    return dot.pick(`ads.${slot}`, settings);
  }, [slot, settings]);
  const hideImageAds = !!getPermission('ads.hide.image');

  if (settings.ads?.disable || isSubscribed || hideImageAds || !adCode) {
    return null;
  }

  return <InvariantAd className={className} slot={slot} adCode={adCode} />;
}

interface InvariantAdProps {
  slot: string;
  adCode: string;
  className?: string;
}
const InvariantAd = memo(
  ({slot, adCode, className}: InvariantAdProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (ref.current) {
        loadAdScripts(adCode, ref.current).then(() => {
          executeAdJavascript(adCode, ref.current!);
        });
      }
      return () => {
        // @ts-ignore
        delete window['google_ad_modifications'];
      };
    }, [adCode]);

    // remove height modifications added by adsense
    useEffect(() => {
      if (ref.current) {
        const scrollParent = getScrollParent(ref.current) as HTMLElement;
        if (scrollParent) {
          const observer = new MutationObserver(function () {
            scrollParent.style.height = '';
            scrollParent.style.minHeight = '';
          });
          observer.observe(scrollParent, {
            attributes: true,
            attributeFilter: ['style'],
          });
          return () => observer.disconnect();
        }
      }
    }, []);

    return (
      <div
        ref={ref}
        className={clsx(
          'ad-host flex max-h-[600px] min-h-90 w-full max-w-full items-center justify-center overflow-hidden',
          `${slot.replace(/\./g, '-')}-host`,
          className,
        )}
        dangerouslySetInnerHTML={{__html: getAdHtml(adCode)}}
      ></div>
    );
  },
  () => {
    // never re-render
    return false;
  },
);

function getAdHtml(adCode: string) {
  // strip out all script tags from ad code and leave only html
  return adCode
    ?.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .trim();
}

// Load any external scripts needed by ad.
function loadAdScripts(adCode: string, parentEl: HTMLDivElement): Promise<any> {
  const promises = [];

  // load ad code script
  const pattern = /<script.*?src=['"](.*?)['"]/g;
  let match;

  while ((match = pattern.exec(adCode))) {
    if (match[1]) {
      promises.push(lazyLoader.loadAsset(match[1], {type: 'js', parentEl}));
    }
  }

  return Promise.all(promises);
}

// Execute ad code javascript and replace document.write if needed.
function executeAdJavascript(adCode: string, parentEl: HTMLDivElement) {
  // find any ad code javascript that needs to be executed
  const pattern = /<script\b(?![^>]*\bsrc=)[^>]*>([\s\S]*?)<\/script>/gi;
  let content;

  while ((content = pattern.exec(adCode))) {
    if (content[1]?.trim()) {
      runInlineAdScript(content[1], parentEl);
    }
  }
}

function runInlineAdScript(scriptContent: string, parentEl: HTMLDivElement) {
  const marker = document.createComment('ad-script-marker');
  parentEl.appendChild(marker);
  const restoreDocumentWrite = interceptDocumentWrite(parentEl, marker);

  try {
    const script = document.createElement('script');
    script.text = scriptContent;
    parentEl.insertBefore(script, marker);
    script.remove();
  } finally {
    restoreDocumentWrite();
    marker.remove();
  }
}

function interceptDocumentWrite(parentEl: HTMLElement, marker: Comment) {
  const originalWrite = document.write.bind(document);
  const originalWriteln = document.writeln.bind(document);

  const insertHtml = (html: string) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const nodes = Array.from(template.content.childNodes);
    nodes.forEach(node => {
      parentEl.insertBefore(node, marker);
    });
  };

  document.write = ((...parts: string[]) => {
    insertHtml(parts.join(''));
  }) as typeof document.write;

  document.writeln = ((...parts: string[]) => {
    insertHtml(`${parts.join('')}\n`);
  }) as typeof document.writeln;

  return () => {
    document.write = originalWrite;
    document.writeln = originalWriteln;
  };
}
